/**
 * Persistent banner shown across every merchant page when the current
 * session is an admin-driven impersonation. Mounted in `MerchantLayout`
 * (and the post-magic-link onboarding layout) so the AM can never
 * forget they are piloting someone else's account.
 *
 * Renders a "fixed-top" amber bar that:
 *   • Names the merchant being viewed
 *   • Counts down to the auto-expiry (60-minute TTL on the server)
 *   • Offers a one-click "Exit preview" that revokes server-side AND
 *     restores the AM back to the admin merchant detail page
 *
 * Zero-impact on real merchant sessions — when localStorage doesn't
 * contain an impersonation flag, this component returns `null`.
 */
import React, { useEffect, useState } from "react";
import { Eye, X, Loader2 } from "lucide-react";
import api from "../../lib/api";
import {
  clearImpersonation,
  impersonationStore,
} from "../../lib/impersonation";


function formatRemaining(expiresAt) {
  try {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (!isFinite(ms) || ms <= 0) return "expired";
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    if (m === 0) return `${s}s`;
    return `${m}m ${s.toString().padStart(2, "0")}s`;
  } catch {
    return "";
  }
}


export default function ImpersonationBanner() {
  const [meta, setMeta] = useState(() => impersonationStore.get());
  const [exiting, setExiting] = useState(false);
  const [, force] = useState(0);

  // Tick the countdown once per second. We also re-read the store on
  // each tick so a clear() from another tab is visible within ~1s.
  useEffect(() => {
    if (!meta) return;
    const t = setInterval(() => {
      const fresh = impersonationStore.get();
      if (!fresh) {
        setMeta(null);
        return;
      }
      force((n) => n + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [meta]);

  if (!meta) return null;

  const exitPreview = async () => {
    if (exiting) return;
    setExiting(true);
    const returnTo = meta.return_to;
    try {
      // Best-effort server revoke. If it fails we still clear locally —
      // the worst case is the token just sits in Mongo until TTL.
      await api.post(`/admin/merchants/${meta.merchant_id}/impersonate/exit`);
    } catch (e) {
      // Swallow — we always clear locally below.
      console.warn("Impersonation exit revoke failed:", e?.message);
    }
    clearImpersonation();
    // Use window.location so the merchant frontend's auth state resets
    // cleanly (clears any in-memory user context, axios interceptors
    // re-read storage on the next request).
    window.location.assign(returnTo || "/admin/merchants");
  };

  const remaining = formatRemaining(meta.expires_at);

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed top-0 inset-x-0 z-[60] bg-amber-400 text-black border-b border-amber-500/60 shadow-[0_2px_10px_rgba(0,0,0,0.25)]"
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Eye className="h-4 w-4 shrink-0" />
          <div className="text-[12.5px] leading-tight min-w-0">
            <div className="font-semibold tracking-tight truncate">
              Viewing as {meta.merchant_legal_name || meta.merchant_email || meta.merchant_id}
            </div>
            <div className="opacity-80 text-[11px]">
              Admin preview · {remaining === "expired" ? "session expired" : `auto-exits in ${remaining}`}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={exitPreview}
          disabled={exiting}
          data-testid="impersonation-exit"
          className="inline-flex items-center gap-1.5 rounded-full bg-black/85 text-white px-3.5 py-1.5 text-[12px] font-medium hover:bg-black disabled:opacity-60 disabled:cursor-wait"
        >
          {exiting ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
          Exit preview
        </button>
      </div>
    </div>
  );
}
